import type { ColumnDef } from '@tanstack/react-table'
import { Code } from 'lucide-react'
import { useMemo } from 'react'
import { AuthoredIcon } from '@/components/admin/shared/authored-icon'
import {
  copyWithToast,
  type DeferredRowClicks,
  useDeferredRowClicks,
} from '@/components/admin/shared/hooks/use-deferred-row-clicks'
import { SourceIconCell } from '@/components/admin/shared/source-icon-cell'
import { SourceIconStack } from '@/components/admin/shared/source-icon-stack'
import {
  compareByFilterMatchCount,
  createDeleteColumn,
  DifficultyBadge,
} from '@/components/admin/shared/table-columns'
import {
  pickSDDisplayCandidate,
  toSDCandidatesFromRouter,
} from '@/lib/problems/sd-source-candidates'
import { Badge } from '../../shared/badge'
import type {
  SystemDesignProblem,
  SystemDesignProblemDifficulty,
} from './use-system-design-problems'

interface UseSDColumnDefinitionsOptions {
  onDeleteProblem: (id: string) => void
  topicsFilter: string[]
}

export function useSDColumnDefinitions({
  onDeleteProblem,
  topicsFilter,
}: UseSDColumnDefinitionsOptions): ColumnDef<SystemDesignProblem>[] {
  const clicks = useDeferredRowClicks()

  return useMemo<ColumnDef<SystemDesignProblem>[]>(
    () => [
      createNameColumn(clicks),
      createDifficultyColumn(),
      createSourceColumn(),
      createCompanyTagsColumn(),
      createTopicsColumn(topicsFilter),
      createEstimatedMinutesColumn(),
      createDeleteColumn<SystemDesignProblem>({
        getName: (problem) => problem.title,
        onDelete: onDeleteProblem,
        title: 'Delete problem',
      }),
    ],
    [onDeleteProblem, topicsFilter, clicks],
  )
}

// =============================================================================
// Column Factory Functions
// =============================================================================

function createNameColumn(
  clicks: DeferredRowClicks,
): ColumnDef<SystemDesignProblem> {
  return {
    id: 'name',
    header: 'Name',
    accessorKey: 'title',
    size: 280,
    cell: ({ row, getValue }) => {
      const allSources = row.original.allSources
      // Which source represents the problem is one question with one answer,
      // shared with the homework row and the editor rather than re-laddered
      // here — including the owning rung, which the list query carries for
      // exactly this.
      const chosen = pickSDDisplayCandidate(
        toSDCandidatesFromRouter(row.original.sourceId, allSources),
      )
      // Only what the shared rule chose. The previous `allSources.find(...)`
      // fallback re-admitted exactly the rows the rule refuses — a soft-deleted
      // source (`sourceSlug === null`) with a stale link won whenever the
      // chosen candidate had no deep link of its own. `siteUrl` is what the
      // candidate already carries for that case.
      const primaryUrl = chosen ? chosen.url || chosen.siteUrl : null
      const title = row.original.title
      const problemId = row.original.id

      const handleCopyName = (e: React.MouseEvent) => {
        e.stopPropagation()
        clicks.onSingleClick(problemId, () => {
          copyWithToast(
            title,
            `Copied "${title}" to clipboard`,
            'Failed to copy to clipboard',
          )
        })
      }

      const handleCopyUrl = (e: React.MouseEvent) => {
        e.stopPropagation()
        clicks.onDoubleClick(problemId, () => {
          if (!primaryUrl) return
          copyWithToast(
            primaryUrl,
            'Copied problem URL to clipboard',
            'Failed to copy URL to clipboard',
          )
        })
      }

      // Check if sources have different titles
      const hasMultipleNames =
        allSources.length > 1 &&
        allSources.some((s) => s.title && s.title !== title)

      // When sources have different names, show each icon paired with its name
      if (hasMultipleNames) {
        return (
          <div className="flex items-center gap-2 text-sm">
            {allSources.map((s, i) => {
              return (
                <span
                  key={s.id}
                  className="flex items-center gap-1"
                  title={s.source}
                >
                  {i > 0 && (
                    <span className="text-muted-foreground mr-1">◆</span>
                  )}
                  <AuthoredIcon
                    icon={s.sourceIcon}
                    className="text-muted-foreground shrink-0 rounded"
                    fallback={Code}
                  />
                  <span
                    className={`truncate ${primaryUrl ? 'text-primary' : 'text-foreground'}`}
                  >
                    {s.title || title}
                  </span>
                </span>
              )
            })}
          </div>
        )
      }

      // Single name: show all source icons overlapping, then the name.
      const iconElement = (
        <SourceIconStack sources={allSources} fallback={Code} />
      )

      return (
        <div className="flex items-center gap-2 text-sm">
          <span className="shrink-0">{iconElement}</span>
          <button
            type="button"
            onClick={handleCopyName}
            onDoubleClick={handleCopyUrl}
            className={`truncate text-left hover:underline ${primaryUrl ? 'text-primary' : 'text-foreground'}`}
            title="Click to copy name, double-click to copy URL"
          >
            {title}
          </button>
        </div>
      )
    },
  }
}

function createDifficultyColumn(): ColumnDef<SystemDesignProblem> {
  return {
    id: 'difficulty',
    header: 'Difficulty',
    accessorKey: 'difficulty',
    size: 100,
    sortingFn: (rowA, rowB) => {
      const a = rowA.original.difficulty?.level ?? -1
      const b = rowB.original.difficulty?.level ?? -1
      return a - b
    },
    // `row.original`, not `getValue()`: the accessor erases to `unknown`, so an
    // assertion here would be re-declaring the router's shape by hand — the
    // exact thing this branch removed everywhere else.
    cell: ({ row }) => <DifficultyBadge difficulty={row.original.difficulty} />,
  }
}

function createSourceColumn(): ColumnDef<SystemDesignProblem> {
  return {
    id: 'source',
    header: 'Source',
    size: 120,
    cell: ({ row }) => {
      const allSources = row.original.allSources
      if (allSources.length > 0) {
        return (
          <div className="flex items-center gap-1">
            {allSources.map((s) => (
              <SourceIconCell
                key={s.id}
                icon={s.sourceIcon}
                name={s.source}
                compact
              />
            ))}
          </div>
        )
      }

      return <span className="text-muted-foreground/50 text-xs">&mdash;</span>
    },
  }
}

function createCompanyTagsColumn(): ColumnDef<SystemDesignProblem> {
  return {
    id: 'companyTags',
    header: 'Company tags',
    // A derived string rather than `accessorKey`: sorting and the table's own
    // filtering both need something comparable, and the column prints the same
    // names it sorts on.
    accessorFn: (row) => row.companyTags.map((tag) => tag.name).join(', '),
    size: 150,
    cell: ({ row }) => {
      const value = row.original.companyTags.map((tag) => tag.name).join(', ')
      return value ? (
        <span className="text-sm">{value}</span>
      ) : (
        <span className="text-muted-foreground/50 text-xs">&mdash;</span>
      )
    },
  }
}

function createTopicsColumn(
  topicsFilter: string[] = [],
): ColumnDef<SystemDesignProblem> {
  return {
    id: 'topics',
    header: 'Topics',
    accessorKey: 'topics',
    size: 200,
    enableSorting: true,
    sortingFn: (rowA, rowB) =>
      compareByFilterMatchCount(
        rowA.original.topics || [],
        rowB.original.topics || [],
        topicsFilter,
      ),
    cell: ({ row }) => {
      const topics = row.original.topics || []
      return (
        <div className="flex flex-wrap gap-1">
          {topics.slice(0, 3).map((topic) => (
            <Badge key={topic} variant="topic" className="text-[10px]">
              {topic}
            </Badge>
          ))}
          {topics.length > 3 && (
            <span className="text-muted-foreground text-xs">
              +{topics.length - 3}
            </span>
          )}
        </div>
      )
    },
  }
}

function createEstimatedMinutesColumn(): ColumnDef<SystemDesignProblem> {
  return {
    id: 'estimatedMinutes',
    header: 'Est. Min',
    accessorKey: 'estimatedMinutes',
    size: 80,
    cell: ({ row }) => {
      const value = row.original.estimatedMinutes
      return value != null ? (
        <span className="text-sm">{value}</span>
      ) : (
        <span className="text-muted-foreground/50 text-xs">&mdash;</span>
      )
    },
  }
}
